import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import type { License, SalesSummary } from '../../api/types';
import { Alert } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, StatCard } from '../../components/ui/Card';
import { StoreBadges } from '../../components/ui/StoreBadges';
import { RegisterCtaButton } from '../../components/layout/RegisterCta';
import { useAuth } from '../../context/AuthContext';
import { CLIENT_APP_LINKS } from '../../config/app-links';
import { defaultDateRange, formatCurrency } from '../../utils/format';

export function DashboardPage() {
  const { user, isDemo } = useAuth();
  const [summary, setSummary] = useState<SalesSummary | null>(null);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const range = defaultDateRange();
    Promise.all([
      api.get<License[]>('/api/license/my-licenses'),
      api.get<SalesSummary>(
        `/api/reports/summary?startDate=${range.startDate}&endDate=${range.endDate}`
      ).catch(() => null),
    ])
      .then(([licenseData, summaryData]) => {
        setLicenses(licenseData);
        if (summaryData) setSummary(summaryData);
      })
      .catch((err: Error) => setError(err.message));
  }, []);

  const activeLicenses = licenses.filter((l) => l.status === 'activa').length;
  const pendingDevices = licenses.filter((l) => !l.hardware_id).length;

  return (
    <div>
      <div className="panel-page-header">
        <div>
          <h1 className="page-title">Hola, {user?.name}</h1>
          <p className="page-subtitle">
            {isDemo
              ? 'Explora el panel con datos de un negocio ficticio de hostelería.'
              : 'Resumen de tu plataforma cloud TPV Hosty.'}
          </p>
        </div>
        {isDemo ? (
          <RegisterCtaButton>Crear mi cuenta</RegisterCtaButton>
        ) : (
          <Link to="/panel/licencias">
            <Button>Nueva licencia</Button>
          </Link>
        )}
      </div>

      {error ? <Alert tone="danger">{error}</Alert> : null}

      <div className="grid-4" style={{ marginBottom: '1.5rem' }}>
        <StatCard
          label="Ventas (30 días)"
          value={summary ? formatCurrency(summary.total_sales) : '—'}
          hint={summary ? `${summary.total_items} tickets` : 'Sin datos aún'}
        />
        <StatCard
          label="Ticket medio"
          value={summary ? formatCurrency(summary.average_sale) : '—'}
        />
        <StatCard label="Licencias activas" value={String(activeLicenses)} />
        <StatCard
          label="TPV pendientes de activar"
          value={String(pendingDevices)}
          hint="Dispositivos sin vincular"
        />
      </div>

      <div className="grid-2">
        <Card title="Accesos rápidos">
          <div className="stack">
            <Link to="/panel/informes">Ver informes detallados</Link>
            <Link to="/panel/dispositivos">Gestionar dispositivos</Link>
            <Link to="/panel/soporte">Soporte técnico</Link>
            <Link to="/descargar">Descargar software TPV</Link>
          </div>
        </Card>
        <Card title={isDemo ? 'Qué puedes probar' : 'Próximo paso con TPV Hosty'}>
          {isDemo ? (
            <p className="muted">
              Navega por Informes para ver gráficos de ventas, métodos de pago y cierres Z de tres
              locales de ejemplo. Las licencias y dispositivos también son ficticios.
            </p>
          ) : (
            <p className="muted">
              Instala el TPV en el equipo del local y actívalo con la clave que encontrarás en
              Licencias. Los cierres Z se sincronizarán aquí automáticamente.
            </p>
          )}
        </Card>
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <Card
          title="App Hosty para tu móvil"
          subtitle="Consulta informes, licencias y soporte desde cualquier lugar"
        >
          <StoreBadges
            appName="Hosty"
            appStoreUrl={CLIENT_APP_LINKS.appStore}
            playStoreUrl={CLIENT_APP_LINKS.playStore}
          />
        </Card>
      </div>
    </div>
  );
}
